import React, { useState } from 'react';
import {
  MdCalendarMonth, MdEdit, MdPlace, MdPhone, MdLanguage, MdCode, MdCopyright,
  MdCheck, MdClose, MdSchool,
} from 'react-icons/md';
import { FaWhatsapp, FaFacebookF, FaInstagram } from 'react-icons/fa';
import useToastWithSound from '../../hooks/useToastWithSound';
import { useAuth } from '../auth';
import useSchoolYear, { saveSchoolYear } from '../useSchoolYear';
import { INTRO_VERSION } from '../intro/version';
import {
  SCHOOL_INFO, DEVELOPER, RIGHTS, yearLabel,
} from '../about';
import { Button } from '../../ui/PageShell';
import { Input } from '../../ui/Form';
import './about.css';

/**
 * حول النظام — who the system belongs to, who built it, the rights
 * notice, and the current school year (the manager can correct its
 * name here; nobody else sees the edit button).
 */

function YearEditor({ year, onDone }) {
  const toast = useToastWithSound();
  const [title, setTitle] = useState(year.title);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const [a, b] = title.trim().split(/\s*[-–/]\s*/);
      await saveSchoolYear(year.id, {
        title: title.trim(),
        start_date: /^\d{4}$/.test(a || '') ? `${a}-09-01` : undefined,
        end_date: /^\d{4}$/.test(b || '') ? `${b}-06-30` : undefined,
      });
      toast.success('تم حفظ العام الدراسي');
      onDone();
    } catch (err) {
      toast.error(err.message || 'تعذّر الحفظ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ab-year-edit" onSubmit={save}>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} dir="ltr" placeholder="2026-2027" autoFocus aria-label="العام الدراسي" />
      <Button type="submit" loading={busy} aria-label="حفظ"><MdCheck /></Button>
      <Button type="button" variant="secondary" onClick={onDone} aria-label="إلغاء"><MdClose /></Button>
    </form>
  );
}

function Link({ href, icon: Icon, children, ltr }) {
  return (
    <a className="ab-link" href={href} target={href.startsWith('tel:') ? undefined : '_blank'} rel="noopener noreferrer">
      <Icon />
      <span dir={ltr ? 'ltr' : undefined}>{children}</span>
    </a>
  );
}

export default function About() {
  const { role } = useAuth();
  const year = useSchoolYear();
  const [editing, setEditing] = useState(false);

  return (
    <div className="ab-page">
      <section className="ab-hero">
        <img className="ab-logo" src={SCHOOL_INFO.logo} alt={`شعار ${SCHOOL_INFO.name}`} />
        <div className="ab-hero-text">
          <h1>{SCHOOL_INFO.name}</h1>
          <p>{SCHOOL_INFO.stages}</p>
          <div className="ab-year">
            <MdCalendarMonth />
            <span>العام الدراسي</span>
            {editing && year ? (
              <YearEditor year={year} onDone={() => setEditing(false)} />
            ) : (
              <>
                <strong dir="ltr">{year ? yearLabel(year.title) : '…'}</strong>
                {role === 'admin' && year && (
                  <button type="button" className="ab-year-btn" onClick={() => setEditing(true)} title="تعديل العام الدراسي" aria-label="تعديل العام الدراسي">
                    <MdEdit />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <div className="ab-grid">
        <section className="ab-card">
          <h2><MdSchool /> المؤسسة</h2>
          <p className="ab-line"><MdPlace /> {SCHOOL_INFO.address}</p>
          <div className="ab-links">
            {SCHOOL_INFO.phones.map((p) => (
              <Link key={p.tel} href={`tel:${p.tel}`} icon={MdPhone} ltr>{p.label}</Link>
            ))}
          </div>
          <div className="ab-links">
            <Link href={SCHOOL_INFO.website} icon={MdLanguage} ltr>mtzedu.com</Link>
            <Link href={SCHOOL_INFO.whatsapp} icon={FaWhatsapp}>واتساب</Link>
            <Link href={SCHOOL_INFO.facebook} icon={FaFacebookF}>فيسبوك</Link>
            <Link href={SCHOOL_INFO.instagram} icon={FaInstagram}>إنستغرام</Link>
            <Link href={SCHOOL_INFO.map} icon={MdPlace}>الموقع على الخريطة</Link>
          </div>
        </section>

        <section className="ab-card ab-dev">
          <h2><MdCode /> المطوّر</h2>
          <div className="ab-dev-head">
            <span className="ab-dev-mark">خ</span>
            <div>
              <strong>{DEVELOPER.name}</strong>
              <span>{DEVELOPER.role}</span>
            </div>
          </div>
          <div className="ab-links">
            <Link href={DEVELOPER.website} icon={MdLanguage} ltr>{DEVELOPER.websiteLabel}</Link>
            <Link href={`tel:${DEVELOPER.phone.tel}`} icon={MdPhone} ltr>{DEVELOPER.phone.label}</Link>
            <Link href={DEVELOPER.whatsapp} icon={FaWhatsapp}>واتساب</Link>
          </div>
        </section>
      </div>

      <section className="ab-card ab-rights">
        <h2><MdCopyright /> الحقوق</h2>
        <p>{RIGHTS.school}</p>
        <p>{RIGHTS.developer}</p>
        <p>{RIGHTS.noCopy}</p>
        <p className="ab-faint">{RIGHTS.thirdParty}</p>
      </section>

      <p className="ab-version" dir="ltr">v{INTRO_VERSION}</p>
    </div>
  );
}
